import { type Selector } from "mathlive";
import React from "react";
import styled from "styled-components";

import {
  IconCursorLeftSolid,
  IconCursorRightSolid,
  IconRedoLine,
  IconUndoLine,
} from "@examspark/common-ui/dist/components/icons";
import Math from "@examspark/common-ui/dist/components/Math";
import { Stem } from "@examspark/types";

const Wrapper = styled.div`
  display: flex;
  margin-top: 0.5rem;
`;

const Styled = styled.div`
  border: solid 1px #ccc;
  border-radius: 0.5rem;
  background-color: #fff;
`;

const Button = styled.button`
  align-items: center;
  background-color: transparent;
  border-radius: 0.5rem;
  border: solid 1px transparent;
  box-sizing: border-box;
  color: #333;
  display: flex;
  font-family: inherit;
  font-size: 1.4rem;
  outline: none;
  transition:
    border-color 0.2s,
    background-color 0.1s,
    color 0.2s;
  white-space: nowrap;
  height: 4.5rem;
  width: 4.5rem;
  padding: 0.8rem;
  gap: 0.8rem;
  justify-content: center;
  cursor: pointer;

  svg {
    fill: currentColor;
  }

  .MathJax_Preview {
    display: none;
  }
`;

const HeaderButton = styled(Button)`
  height: 3.5rem;
  width: 3.5rem;

  &:hover {
    background-color: #e2e2e2;
  }
`;
const NumericButton = styled(Button)`
  background-color: #fcfcda;
  border: solid 1px #cccccc;

  &:hover {
    border-color: #888;
    box-shadow: 0.1rem 0.1rem 0.1rem rgba(0, 0, 0, 0.1);
  }
`;
const OperationButton = styled(Button)`
  background-color: #f2f2f2;
  border: solid 1px #cccccc;
  font-size: 2rem;

  &:hover {
    border-color: #888;
    box-shadow: 0.1rem 0.1rem 0.1rem rgba(0, 0, 0, 0.1);
  }
`;
const MathButton = styled(Button)`
  background-color: #f3f0fb;
  border: solid 1px #cccccc;
  font-size: 1.2rem;

  &:hover {
    border-color: #888;
    box-shadow: 0.1rem 0.1rem 0.1rem rgba(0, 0, 0, 0.1);
  }
`;

const Header = styled.div`
  padding: 0.5rem;
  border-bottom: solid 1px #ddd;
  display: flex;
  align-items: center;
`;

const Spacer = styled.div`
  flex: 1;
`;
const Body = styled.div`
  padding: 0.5rem;
  display: flex;
  gap: 1.5rem;
`;
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
`;
const Row = styled.div`
  display: flex;
  gap: 0.5rem;
  min-width: 0;
`;
const Columns = styled.div`
  display: flex;
  gap: 0.5rem;
`;
const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
`;

export interface KeypadProps {
  onExecuteCommand?: (command: Selector) => void;
  onInsert?: (value: string) => void;
  layout?: Stem.MathKeyboardPreset;
}

const buttonProps: (
  props: React.HTMLAttributes<HTMLButtonElement>,
) => React.HTMLAttributes<HTMLButtonElement> = (
  props: React.HTMLAttributes<HTMLButtonElement>,
) => ({
  onMouseDown: (e) => e.preventDefault(),
  ...props,
});

const EquationEntryKeypad: React.FC<React.PropsWithChildren<KeypadProps>> = (
  props,
) => (
  <Wrapper data-cy="math-keypad">
    <Styled>
      <Header>
        <HeaderButton
          title="Move to previous character"
          {...buttonProps({
            onClick: () => props.onExecuteCommand?.("moveToPreviousChar"),
          })}
        >
          <IconCursorLeftSolid width="10px" />
        </HeaderButton>
        <HeaderButton
          title="Move to next character"
          {...buttonProps({
            onClick: () => props.onExecuteCommand?.("moveToNextChar"),
          })}
        >
          <IconCursorRightSolid width="10px" />
        </HeaderButton>
        <Spacer></Spacer>
        <HeaderButton
          title="Delete character"
          {...buttonProps({
            onClick: () => props.onExecuteCommand?.("deleteBackward"),
          })}
        >
          {/* todo: move this icon I stole from the graphing item types to common-ui */}
          <svg viewBox="0 0 421.542 421.542" width="18px">
            <path
              id="path1155"
              d="m106.584 61.396c-24.196 0-46.346 13.775-57.167 35.417l-42.667 85.375c-9 18.001-9 39.166 0 57.167l42.667 85.333c10.821 21.642 32.97 35.458 57.167 35.458h251.042c35.094 0 63.917-28.99 63.917-64.083v-170.709c0-35.094-28.823-63.958-63.917-63.958zm95.833 85.042c5.785.044 11.303 2.435 15.292 6.625l27.542 27.5 27.542-27.5c3.864-3.977 9.124-6.293 14.667-6.458 11.778-.346 21.607 8.921 21.953 20.699.175 5.937-2.134 11.678-6.37 15.842l-27.583 27.583 27.583 27.5c8.353 8.353 8.353 21.897 0 30.25s-21.897 8.353-30.25 0l-27.542-27.542-27.542 27.542c-8.353 8.353-21.897 8.353-30.25 0s-8.353-21.897 0-30.25l27.583-27.5-27.583-27.583c-8.491-8.17-8.75-21.677-.58-30.167 4.062-4.221 9.68-6.586 15.538-6.541z"
            />
          </svg>
        </HeaderButton>
        <HeaderButton
          title="Undo"
          {...buttonProps({
            onClick: () => props.onExecuteCommand?.("undo"),
          })}
        >
          <IconUndoLine width="16" />
        </HeaderButton>
        <HeaderButton
          title="Redo"
          {...buttonProps({
            onClick: () => props.onExecuteCommand?.("redo"),
          })}
        >
          <IconRedoLine width="16" />
        </HeaderButton>
      </Header>
      <Body>
        <Section>
          <Row>
            <NumericButton
              {...buttonProps({
                onClick: () => props.onInsert?.("7"),
              })}
            >
              7
            </NumericButton>
            <NumericButton
              {...buttonProps({
                onClick: () => props.onInsert?.("8"),
              })}
            >
              8
            </NumericButton>
            <NumericButton
              {...buttonProps({
                onClick: () => props.onInsert?.("9"),
              })}
            >
              9
            </NumericButton>
            <OperationButton
              {...buttonProps({
                onClick: () => props.onInsert?.("\\div"),
              })}
            >
              ÷
            </OperationButton>
          </Row>
          <Row>
            <NumericButton
              {...buttonProps({
                onClick: () => props.onInsert?.("4"),
              })}
            >
              4
            </NumericButton>
            <NumericButton
              {...buttonProps({
                onClick: () => props.onInsert?.("5"),
              })}
            >
              5
            </NumericButton>
            <NumericButton
              {...buttonProps({
                onClick: () => props.onInsert?.("6"),
              })}
            >
              6
            </NumericButton>
            <OperationButton
              {...buttonProps({
                onClick: () => props.onInsert?.("\\times"),
              })}
            >
              ×
            </OperationButton>
          </Row>
          <Row>
            <NumericButton
              {...buttonProps({
                onClick: () => props.onInsert?.("1"),
              })}
            >
              1
            </NumericButton>
            <NumericButton
              {...buttonProps({
                onClick: () => props.onInsert?.("2"),
              })}
            >
              2
            </NumericButton>
            <NumericButton
              {...buttonProps({
                onClick: () => props.onInsert?.("3"),
              })}
            >
              3
            </NumericButton>
            <OperationButton
              {...buttonProps({
                onClick: () => props.onInsert?.("-"),
              })}
            >
              -
            </OperationButton>
          </Row>
          <Row>
            <NumericButton
              {...buttonProps({
                onClick: () => props.onInsert?.("."),
              })}
            >
              .
            </NumericButton>
            <NumericButton
              {...buttonProps({
                onClick: () => props.onInsert?.("0"),
              })}
            >
              0
            </NumericButton>
            <OperationButton
              {...buttonProps({
                onClick: () => props.onInsert?.("="),
              })}
            >
              =
            </OperationButton>
            <OperationButton
              {...buttonProps({
                onClick: () => props.onInsert?.("+"),
              })}
            >
              +
            </OperationButton>
          </Row>
        </Section>
        <Section>
          <Columns>
            <Column>
              <MathButton
                {...buttonProps({
                  onClick: () => props.onInsert?.("\\gt"),
                })}
              >
                <Math tex={"\\gt"} />
              </MathButton>
              <MathButton
                {...buttonProps({
                  onClick: () => props.onInsert?.("\\lt"),
                })}
              >
                <Math tex={"\\lt"} />
              </MathButton>
              <MathButton
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => props.onInsert?.("\\frac")}
              >
                <Math
                  tex={`\\frac{\\bbox[4px, border: 1px dashed gray]{}}{\\bbox[4px, border: 1px dashed gray]{}}`}
                />
              </MathButton>
              <MathButton
                {...buttonProps({
                  onClick: () => props.onInsert?.("\\left(#0\\right)"),
                })}
              >
                <Math
                  tex={`\\left(\\bbox[border: 1px dashed grey]{\\phantom{o}}\\right)`}
                />
              </MathButton>
            </Column>
            {props.layout === "secondary" && (
              <>
                <Column>
                  <MathButton
                    {...buttonProps({
                      onClick: () => props.onInsert?.("\\ge"),
                    })}
                  >
                    <Math tex={"\\ge"} />
                  </MathButton>
                  <MathButton
                    {...buttonProps({
                      onClick: () => props.onInsert?.("\\le"),
                    })}
                  >
                    <Math tex={"\\le"} />
                  </MathButton>
                  <MathButton
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => props.onInsert?.("^{#0}")}
                  >
                    <Math tex={`x^{y}`} />
                  </MathButton>
                  <MathButton
                    {...buttonProps({
                      onClick: () => props.onInsert?.("_{#0}"),
                    })}
                  >
                    <Math tex={`x_{y}`} />
                  </MathButton>
                </Column>
                <Column>
                  <MathButton
                    {...buttonProps({
                      onClick: () => props.onInsert?.("x"),
                    })}
                  >
                    <Math tex={"x"} />
                  </MathButton>
                  <MathButton
                    {...buttonProps({
                      onClick: () => props.onInsert?.("y"),
                    })}
                  >
                    <Math tex={"y"} />
                  </MathButton>
                  <MathButton
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => props.onInsert?.("z")}
                  >
                    <Math tex={`z`} />
                  </MathButton>
                  <MathButton
                    {...buttonProps({
                      onClick: () => props.onInsert?.("\\sqrt{#0}"),
                    })}
                  >
                    <Math
                      tex={`\\sqrt{\\bbox[4px, border: 1px dashed gray]{}}`}
                    />
                  </MathButton>
                </Column>
                <Column>
                  <MathButton
                    {...buttonProps({
                      onClick: () => props.onInsert?.("\\pi"),
                    })}
                  >
                    <Math tex={"\\pi"} />
                  </MathButton>
                </Column>
              </>
            )}
          </Columns>
        </Section>
      </Body>
    </Styled>
  </Wrapper>
);

export default EquationEntryKeypad;
